import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { jobs } from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, MapPin, Clock, PoundSterling } from 'lucide-react';

const JobDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadJob();
    }, [id]);

    const loadJob = async () => {
        try {
            const res = await jobs.getOne(id);
            setJob(res.data);
            
            if (user.user_type === 'company') {
                const matchesRes = await jobs.getMatches(id);
                setMatches(matchesRes.data.matches || []);
            }
        } catch (error) {
            toast.error('Failed to load job');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#8B5CF6] border-t-transparent" />
        </div>
    );
    if (!job) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>Job not found</div>;

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 md:px-8 py-10">
                <div className="bg-white rounded-3xl p-8 border border-purple-200/50 shadow-sm mb-8">
                    <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Role Details
                    </span>
                    <h1 className="text-4xl font-bold mb-3 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="job-title">{job.title}</h1>
                    <p className="text-lg text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>{job.company_name}</p>
                    
                    <div className="flex flex-wrap gap-4 mb-8">
                        <div className="flex items-center space-x-2 bg-[#ECFDF5] px-3 py-1.5 rounded-full">
                            <PoundSterling className="h-4 w-4 text-[#10B981]" />
                            <span className="font-semibold text-sm text-[#059669]" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{job.pay_rate}/hr</span>
                        </div>
                        {job.salary && (
                            <div className="flex items-center space-x-2 bg-purple-50 px-3 py-1.5 rounded-full">
                                <Briefcase className="h-4 w-4 text-[#8B5CF6]" />
                                <span className="font-semibold text-sm text-[#7C3AED]">{job.salary}</span>
                            </div>
                        )}
                        <div className="flex items-center space-x-2 bg-slate-50 px-3 py-1.5 rounded-full">
                            <Clock className="h-4 w-4 text-slate-500" />
                            <span className="text-sm text-slate-600">{job.trial_length_days} days trial</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-slate-50 px-3 py-1.5 rounded-full">
                            <MapPin className="h-4 w-4 text-slate-500" />
                            <span className="text-sm text-slate-600">{job.location}</span>
                        </div>
                        <span className="bg-purple-100 text-[#8B5CF6] px-3 py-1.5 rounded-full text-sm font-semibold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            {job.sector}
                        </span>
                    </div>

                    <div className="mb-6">
                        <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Description</h2>
                        <p className="text-slate-600 whitespace-pre-line leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>{job.description}</p>
                    </div>

                    <div className="mb-6">
                        <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Required Skills</h2>
                        <div className="flex flex-wrap gap-2">
                            {job.skills.map((skill, idx) => (
                                <span key={idx} className="bg-[#F3E8FF] text-[#7C3AED] px-3 py-1.5 rounded-full text-sm font-medium">{skill}</span>
                            ))}
                        </div>
                    </div>

                    {user.user_type === 'company' && job.estimated_cost && (
                        <div className="rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)' }}>
                            <span className="uppercase tracking-[0.15em] text-xs font-semibold text-purple-300 block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>Cost Breakdown</span>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-purple-200">Trial Cost</span>
                                    <span className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{job.estimated_cost.trial_cost}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-purple-200">Hire Fee (if hired)</span>
                                    <span className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{job.estimated_cost.hire_fee}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {user.user_type === 'company' && matches.length > 0 && (
                    <div>
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            AI Matches
                        </span>
                        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Matched Candidates</h2>
                        <div className="grid grid-cols-1 gap-4">
                            {matches.map((candidate, idx) => {
                                if (!candidate || typeof candidate !== 'object') return null;
                                const safeId = candidate.id || candidate.candidate_id || `match-${idx}`;
                                const fullName = typeof candidate.name === 'string' && candidate.name.trim() ? candidate.name : 'Candidate';
                                const firstName = fullName.split(' ')[0] || 'Candidate';
                                const email = typeof candidate.email === 'string' ? candidate.email : '';
                                const skills = Array.isArray(candidate.skills) ? candidate.skills : [];
                                const experience = typeof candidate.experience === 'string' ? candidate.experience : '';
                                return (
                                    <div key={safeId} className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>#{idx + 1} - {firstName}</h3>
                                                {email && <p className="text-sm text-slate-500 mb-2">{email}</p>}
                                                {skills.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mb-3">
                                                        {skills.map((skill, sidx) => (
                                                            <span key={sidx} className="bg-[#F3E8FF] text-[#7C3AED] px-2 py-1 rounded-full text-xs font-medium">{skill}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {experience && <p className="text-sm text-slate-600">{experience}</p>}
                                            </div>
                                            <Button className="btn-primary" onClick={() => navigate('/trials')} data-testid={`invite-btn-${safeId}`}>
                                                Invite to Trial
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default JobDetails;
